import { canvasManager } from "appworks/graphics/canvas/canvas-manager";
import { Orientation } from "appworks/graphics/canvas/orientation";
import { graphicsManager } from "appworks/graphics/graphics-manager";
import { CenterPivot } from "appworks/graphics/pixi/group";
import { Contract } from "appworks/utils/contracts/contract";
import { Rectangle } from "appworks/utils/geom/rectangle";
import * as TweenJS from "appworks/utils/tween";
import { AbstractSymbolBehaviour } from "slotworks/components/matrix/symbol/behaviours/abstract-symbol-behaviour";

export class LandPulseSymbolBehaviour extends AbstractSymbolBehaviour {
    public static ANIMATION_DURATION: number = 200;

    public static ZOOM_TO: number = 1.1;

    public behaviourName: string = "LandPulse";

    protected masterSymbol: string = "symbol_0_0";

    private scale: number;

    private offset: Rectangle;

    public static(): void {
        super.static();
        this.cleanup();
        this.symbol.setVisible(true);
    }

    public land(): Contract<void> {
        return new Contract<void>((resolve) => {
            this.createSprite();

            this.symbol.setVisible(false);

            const originalScale = this.behaviourSprite.scale.x;
            this.scale = originalScale;

            const orientation = canvasManager.orientation === Orientation.LANDSCAPE ? "landscape" : "portrait";

            const masterRect = this.matrixLayer.getPosition(this.masterSymbol)[orientation];
            let animRect = this.matrixLayer.getPosition(this.symbol.symbolId)?.[orientation];

            if (!animRect) {
                animRect = this.matrixLayer.getPosition("static")[orientation];
            }

            const offsetX = animRect.x - masterRect.x;
            const offsetY = animRect.y - masterRect.y;
            this.offset = new Rectangle(
                offsetX + (animRect.width / 2),
                offsetY + (animRect.height / 2),
                (animRect.width / masterRect.width),
                (animRect.height / masterRect.height)
            );

            const growTween = new TweenJS.Tween(this)
                .to({ scale: originalScale * LandPulseSymbolBehaviour.ZOOM_TO }, LandPulseSymbolBehaviour.ANIMATION_DURATION / 2);

            const shrinkTween = new TweenJS.Tween(this)
                .to({ scale: originalScale }, LandPulseSymbolBehaviour.ANIMATION_DURATION / 2);

            growTween.chain(shrinkTween);

            shrinkTween.onComplete(() => {
                this.cleanup();
                this.symbol.setVisible(true);
                resolve();
            });

            growTween.start();
        });
    }

    public updateTransform() {
        super.updateTransform();

        if (this.behaviourSprite) {
            this.behaviourSprite.landscape.x += this.offset.x;
            this.behaviourSprite.landscape.y += this.offset.y;
            this.behaviourSprite.landscape.width *= this.offset.width * this.scale;
            this.behaviourSprite.landscape.height *= this.offset.height * this.scale;

            // Adjust for if this is the middle of a stack
            let stackOffset = this.symbol.getStackOffsetLandscapeY();
            if (stackOffset) {
                this.behaviourSprite.landscape.y -= stackOffset;
            }

            this.behaviourSprite.portrait.x += this.offset.x;
            this.behaviourSprite.portrait.y += this.offset.y;
            this.behaviourSprite.portrait.width *= this.offset.width * this.scale;
            this.behaviourSprite.portrait.height *= this.offset.height * this.scale;

            // Adjust for if this is the middle of a stack
            stackOffset = this.symbol.getStackOffsetLandscapeY();
            if (stackOffset) {
                this.behaviourSprite.portrait.y -= stackOffset;
            }
        }
    }

    private createSprite() {
        this.cleanup();

        this.behaviourSprite = graphicsManager.createSprite(this.symbol.symbolId);
        CenterPivot(this.behaviourSprite);
        this.animationLayer.add(this.behaviourSprite);
    }

    private cleanup() {
        if (this.behaviourSprite) {
            this.animationLayer.remove(this.behaviourSprite);
            this.behaviourSprite.destroy();
            this.behaviourSprite = undefined;
        }
    }
}
